import React, { useEffect, useState } from "react";
import { Button } from "../../shadcn/components/ui/button";
import { Textarea } from "../../shadcn/components/ui/textarea";
import CustomModalLayoutV2 from "../bidder/Tabs/auctionFloor/components/CustomModalLayoutV2";
import PDFViewer from "./bidder/PDFViewer";
import { approveApprovalRequest, rejectApprovalRequest } from "services";
import { Download, Eye } from "lucide-react";

interface KYCApprovalComponentProps {
  data: any;
  encryptedKey: string;
}

interface DocumentViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentUrls: string[];
  currentDocumentIndex: number;
  onPrevious: () => void;
  onNext: () => void;
  headingText?: string;
}

function DocumentViewerModal({
  isOpen,
  onClose,
  documentUrls,
  currentDocumentIndex,
  onPrevious,
  onNext,
  headingText = "KYC Documents",
}: DocumentViewerModalProps) {
  const [failedImageUrls, setFailedImageUrls] = useState<Set<string>>(
    new Set(),
  );

  const currentUrl = documentUrls[currentDocumentIndex];
  const hasImageLoadError = currentUrl
    ? failedImageUrls.has(currentUrl)
    : false;

  const handleImageError = (url: string) => {
    setFailedImageUrls((prev) => new Set(prev).add(url));
  };

  const handleClose = () => {
    setFailedImageUrls(new Set());
    onClose();
  };

  return (
    <CustomModalLayoutV2
      open={isOpen}
      onClose={handleClose}
      blurBackground={true}
      className="!w-full mx-2 sm:mx-4 md:mx-6 max-w-[580px] max-h-[95vh] sm:max-h-[90vh] overflow-hidden"
      childrenClassName="py-4 px-0 overflow-hidden flex flex-col"
    >
      <div className="flex flex-col h-full max-h-[95vh] sm:max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 border-b flex-shrink-0 bg-white">
          <h3 className="text-lg sm:text-xl font-semibold text-brand-textBlack">
            {headingText}
          </h3>
          {documentUrls.length > 1 && (
            <div className="text-xs sm:text-sm font-medium text-gray-600 bg-gray-100 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full">
              {currentDocumentIndex + 1} / {documentUrls.length}
            </div>
          )}
        </div>

        {/* Document Content Area */}
        <div className="flex-1 overflow-auto relative min-h-0 bg-gray-50">
          {documentUrls.length > 0 && currentUrl ? (
            !hasImageLoadError ? (
              <div className="flex items-start justify-center w-full min-h-full p-2 sm:p-4">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={currentUrl}
                  alt={`KYC Document ${currentDocumentIndex + 1}`}
                  className="max-w-full w-auto h-auto object-contain rounded-lg shadow-sm"
                  onError={() => handleImageError(currentUrl)}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center w-full h-full p-2 sm:p-4">
                <div className="w-full h-full rounded-lg shadow-sm overflow-hidden bg-white">
                  <PDFViewer pdf_url={currentUrl} />
                </div>
              </div>
            )
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <p>No document available</p>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        {documentUrls.length > 1 && (
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-t bg-white flex-shrink-0 gap-2 sm:gap-0">
            <Button
              onClick={onPrevious}
              disabled={documentUrls.length === 0}
              className="flex-1 sm:flex-initial text-xs sm:text-sm"
            >
              Previous
            </Button>
            <Button
              onClick={onNext}
              disabled={documentUrls.length === 0}
              className="flex-1 sm:flex-initial text-xs sm:text-sm"
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </CustomModalLayoutV2>
  );
}

export default function KYCApprovalComponent({
  data,
  encryptedKey,
}: KYCApprovalComponentProps) {
  const [loading, setLoading] = useState(false);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [documentUrls, setDocumentUrls] = useState<string[]>([]);
  const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [downloadDocumentType, setDownloadDocumentType] = useState<
    | "id"
    | "address"
    | "ar_kyc_doc"
    | "authorization_letters"
    | "other_docs"
    | null
  >(null);
  const [modelHeading, setModelHeading] = useState("KYC Documents");
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [rejectRemark, setRejectRemark] = useState("");
  const [approveRemark, setApproveRemark] = useState("");
  const [isKycProcessed, setIsKycProcessed] = useState(false);
  const [status, setStatus] = useState<"approved" | "rejected" | null>(null);

  // Helper functions
  const extractFileId = (file: string | { id?: string } | any): string => {
    if (typeof file === "string") return file;
    return file?.id || file || "";
  };

  const buildFileUrl = (fileId: string, download = false): string => {
    const baseUrl = fileId;
    return download ? `${baseUrl}?dl` : baseUrl;
  };

  const handleDownloadFile = (fileId: string, index: number): void => {
    const fileUrl = buildFileUrl(fileId, true);
    let filename = `kyc-document-${index + 1}`;

    if (downloadDocumentType === "ar_kyc_doc") {
      filename = `ar-kyc-document-${index + 1}`;
    } else if (downloadDocumentType === "authorization_letters") {
      filename = `authorization-letter-${index + 1}`;
    } else if (downloadDocumentType === "other_docs") {
      filename = `other-supporting-document-${index + 1}`;
    }

    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewDocuments = async (
    type:
      | "id"
      | "address"
      | "ar_kyc_doc"
      | "authorization_letters"
      | "other_docs",
  ) => {
    let urls: any = [];
    if (type === "id") {
      urls = data?.data?.id_proof_docs || [];
      setModelHeading("ID Proof Documents");
    } else if (type === "address") {
      urls = data?.data?.address_proof_docs || [];
      setModelHeading("Address Proof Documents");
    } else if (type === "ar_kyc_doc") {
      urls = data?.data?.ar_docs?.map((file: any) => file) || [];
      setModelHeading("AR KYC Documents");
    } else if (type === "authorization_letters") {
      urls =
        data?.data?.ar_authorization_letter_docs?.map((file: any) => file) ||
        [];
      setModelHeading("Authorization Letters");
    } else if (type === "other_docs") {
      urls = data?.data?.additional_docs?.map((file: any) => file) || [];
    }

    if (!urls || urls.length === 0) {
      return;
    }

    setDocumentUrls(urls);
    setCurrentDocumentIndex(0);
    setIsDocumentModalOpen(true);
  };

  const handleDownloadDocuments = async (
    type:
      | "id"
      | "address"
      | "ar_kyc_doc"
      | "authorization_letters"
      | "other_docs",
  ) => {
    let urls: any = [];
    if (type === "id") {
      urls = data?.data?.id_proof_docs || [];
      setDownloadDocumentType("id");
      setModelHeading("ID Proof Documents");
    } else if (type === "address") {
      urls = data?.data?.address_proof_docs || [];
      setDownloadDocumentType("address");
      setModelHeading("Address Proof Documents");
    } else if (type === "ar_kyc_doc") {
      urls = data?.data?.ar_docs?.map((file: any) => file) || [];
      setDownloadDocumentType("ar_kyc_doc");
      setModelHeading("AR KYC Documents");
    } else if (type === "authorization_letters") {
      urls =
        data?.data?.ar_authorization_letter_docs?.map((file: any) => file) ||
        [];
      setDownloadDocumentType("authorization_letters");
      setModelHeading("Authorization Letters");
    } else if (type === "other_docs") {
      urls = data?.data?.additional_docs?.map((file: any) => file) || [];
      setDownloadDocumentType("other_docs");
      setModelHeading("Other Supporting Documents");
    }

    if (!urls || urls.length === 0) {
      return;
    }

    setDocumentUrls(urls);
    setIsDownloadModalOpen(true);
  };

  const handleApprove = async () => {
    try {
      setLoading(true);
      let payload: any = {
        token: encryptedKey,
      };
      if (approveRemark.trim()) {
        payload["remarks"] = approveRemark.trim();
      }
      const response = await approveApprovalRequest(payload);
      console.log("response: ", response);
      setIsKycProcessed(true);
      setStatus("approved");
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setLoading(false);
      setApproveRemark("");
      setIsApproveModalOpen(false);
    }
  };

  const handleReject = async () => {
    if (!rejectRemark.trim()) {
      return; // Don't proceed if remark is empty
    }
    try {
      setLoading(true);
      const response = await rejectApprovalRequest({
        token: encryptedKey,
        remarks: rejectRemark,
      });
      console.log("response: ", response);
      setIsRejectModalOpen(false);
      setRejectRemark("");
      setIsKycProcessed(true);
      setStatus("rejected");
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRejectClick = () => {
    setIsRejectModalOpen(true);
  };

  if (isKycProcessed) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] px-6 text-center">
        <div className="bg-white p-8 rounded-2xl shadow-md max-w-md w-full">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">
            {status === "approved" ? "KYC Approved" : "KYC Rejected"}
          </h2>
          <p className="text-gray-600 mb-6">
            The bidder has been notified about your decision.
          </p>
          <p className="text-sm text-gray-500">You can now close this page.</p>
        </div>
      </div>
    );
  }
  const maskEmail = (email: any) => {
    if (!email) return "";
    const [local, domain] = email.split("@");
    if (!local || !domain) return "";

    // Mask local part: show middle character with asterisks before and after
    // Format: ***v**** (3+ asterisks, middle char, 4+ asterisks)
    let maskedLocal = "***";
    if (local.length > 2) {
      const middleIndex = Math.floor(local.length / 2);
      maskedLocal = `***${local[middleIndex]}***`;
    } else if (local.length > 0) {
      maskedLocal = "***";
    }

    // Mask domain part: show middle character of domain name, mask rest, then show TLD
    // Format: ***i***.com (3+ asterisks, middle char, 3+ asterisks, TLD)
    const [domainName, ...tldParts] = domain.split(".");
    const tld = tldParts.length > 0 ? `.${tldParts.join(".")}` : "";
    let maskedDomain = "***";
    if (domainName.length > 2) {
      const middleIndex = Math.floor(domainName.length / 2);
      maskedDomain = `***${domainName[middleIndex]}***${tld}`;
    } else if (domainName.length > 0) {
      maskedDomain = `***${tld}`;
    }
    return `${maskedLocal}@${maskedDomain}`;
  };

  const maskPhone = (phone: string) => {
    if (!phone) return "";
    return "+91" + phone.slice(0, 1) + "********" + phone.slice(-1);
  };
  const maskName = (name: string) => {
    if (!name || name.length === 0) return "";
    if (name.length <= 2) return name.charAt(0) + "***";
    return name.charAt(0) + "**********" + name.charAt(name.length - 1);
  };
  const maskPan = (pan: any) => {
    if (!pan) return "";
    const panStr = String(pan).toUpperCase();
    if (panStr.length < 4) return "****";
    if (panStr.length <= 4) return panStr.slice(0, 2) + "**";
    const first2 = panStr.slice(0, 1);
    const last2 = panStr.slice(-1);
    return `${first2}********${last2}`;
  };

  return (
    <div className="min-h-screen flex flex-col items-center py-4 px-4 border-[1px] rounded-sm font-openSans">
      <div className="bg-white w-full px-0 sm:px-4 py-2 md:px-8">
        <h3 className="text-xl font-semibold text-brand-textBlack mb-6 text-center md:text-left">
          KYC Approval
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 border-y-[1px] py-4 mb-4">
          <div className="px-2 border-b md:border-b-[0px] md:border-r">
            <div>
              <h4 className="text-md font-semibold text-brand-textBlack mb-3">
                Bidder Details
              </h4>
              <ul className="space-y-2 text-brand-textBlack text-sm pb-4 border-b">
                <li>
                  <span className="font-medium text-brand-orange mr-2">
                    Bidder Name:
                  </span>{" "}
                  {data?.data?.name || ""}
                </li>
                <li>
                  <span className="font-medium text-brand-orange mr-2">
                    Bidder Mobile:
                  </span>{" "}
                  {data?.data?.mobile_number || ""}
                </li>
                <li>
                  <span className="font-medium text-brand-orange mr-2">
                    Bidder Email id:
                  </span>{" "}
                  {data?.data?.email || ""}
                </li>
                <li>
                  <span className="font-medium text-brand-orange mr-2">
                    PAN Number:
                  </span>{" "}
                  {data?.data?.pan || ""}
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-md font-semibold text-brand-textBlack mb-3">
                Event Details
              </h4>
              <ul className="space-y-2 text-brand-textBlack text-sm pb-4 border-b">
                <li>
                  <span className="font-medium text-brand-orange mr-2 ">
                    Event Name:
                  </span>{" "}
                  <span className="capitalize text-xs font-semibold tracking-wide">
                    {data?.data?.event_name || ""}
                  </span>
                </li>
                <li>
                  <span className="font-medium text-brand-orange mr-2">
                    Event Id:
                  </span>{" "}
                  <a
                    href={`${
                      process.env.NEXT_PUBLIC_BIDDER_DB_URL?.includes(
                        "auctionbazaar",
                      )
                        ? `https://www.auctionbazaar.com/auction-properties/${data?.data?.property_listing_id}`
                        : `https://quickbid.in/auction-properties/${data?.data?.property_listing_id}`
                    }`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline cursor-pointer text-xs font-semibold tracking-wider text-brand-headingBlue"
                  >
                    {data?.data?.event_listing_id || ""}
                  </a>
                </li>
                <li className="capitalize">
                  <span className="font-medium text-brand-orange mr-2">
                    Borrower Name:
                  </span>{" "}
                  {data?.data?.borrower_name || ""}
                </li>
                <li>
                  <span className="font-medium text-brand-orange mr-2">
                    EMD Last Date:
                  </span>{" "}
                  {data?.data?.emd_last_datetime || ""}
                </li>
                <li>
                  <span className="font-medium text-brand-orange mr-2">
                    Auction start Date:
                  </span>{" "}
                  {data?.data?.auction_start_datetime || ""}
                </li>
              </ul>
            </div>
          </div>

          {/* KYC Documents Viewer */}
          <div className="relative py-4 md:py-0">
            <div className="px-2">
              <h4 className="text-md font-semibold text-brand-textBlack mb-2">
                KYC Documents
              </h4>

              {/* Name Mismatch Alert */}
              {data?.data?.is_name_mismatch ? (
                <>
                  <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-2 rounded mb-4 flex items-center gap-2 text-xs md:text-sm">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="font-semibold">
                      Name mismatch - pending approval
                    </span>
                  </div>
                  <div className="bg-gray-100 text-gray-700 text-sm p-3 rounded mb-3">
                    KYC Alert: Name mismatch between Aadhaar, PAN, And Profile.
                  </div>
                </>
              ) : (
                <div className="bg-brand-bgSuccessGreen border border-brand-bgSuccessGreen text-brand-bgWhite px-3 py-1 rounded mb-4 flex items-center gap-2 text-xs md:text-sm">
                  <span className="">Name Matching in Documents</span>
                </div>
              )}

              {/* PAN / Aadhaar Section */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-1 mb-2">
                <div className="flex items-center justify-between">
                  <h5 className="text-xs md:text-sm font-semibold text-brand-textBlack">
                    ID Proof
                  </h5>
                  <div className="flex items-center gap-2">
                    <button
                      className="p-2 rounded-full hover:bg-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      title="View All Documents"
                      onClick={async () => await handleViewDocuments("id")}
                      disabled={
                        !data?.data?.id_proof_docs ||
                        data?.data?.id_proof_docs.length === 0
                      }
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-brand-headingBlue"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => {
                        handleDownloadDocuments("id");
                        // setIsDownloadModalOpen(true);
                      }}
                      className="p-2 rounded-full hover:bg-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      title={`Download KYC document${
                        data?.data?.id_proof_docs?.length > 1 ? "s" : ""
                      }`}
                      disabled={
                        !data?.data?.id_proof_docs ||
                        data?.data?.id_proof_docs.length === 0
                      }
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-brand-headingBlue"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="text-xs text-[#777]">Details shown in UI.</div>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg py-1 px-4 mb-2">
                <div className="flex items-center justify-between">
                  <h5 className="text-xs md:text-sm font-semibold text-brand-textBlack">
                    Address Proof
                  </h5>
                  <div className="flex items-center gap-2">
                    <button
                      className="p-2 rounded-full hover:bg-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      title="View All Documents"
                      onClick={async () => {
                        await handleViewDocuments("address");
                      }}
                      disabled={
                        !data?.data?.address_proof_docs ||
                        data?.data?.address_proof_docs.length === 0
                      }
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-brand-headingBlue"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => {
                        handleDownloadDocuments("address");
                        // setIsDownloadModalOpen(true);
                      }}
                      className="p-2 rounded-full hover:bg-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      title={`Download KYC document${
                        data?.data?.address_proof_docs?.length > 1 ? "s" : ""
                      }`}
                      disabled={
                        !data?.data?.address_proof_docs ||
                        data?.data?.address_proof_docs.length === 0
                      }
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-brand-headingBlue"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="text-xs text-[#777]">
                  Masked Details shown in UI.
                </div>
              </div>
              {data?.data?.is_name_mismatch && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                  <div className="flex items-center justify-between mb-1">
                    <h5 className="font-semibold text-brand-textBlack text-base">
                      Name Mismatch Declaration
                    </h5>
                    {data?.data?.name_mismatch_docs &&
                      data?.data?.name_mismatch_docs.length > 0 && (
                        <a
                          href={`${
                            data?.data?.name_mismatch_docs[0]
                          }?dl&filename=name-mismatch-declaration-${Date.now()}.pdf`}
                          download={`name-mismatch-declaration-${Date.now()}.pdf`}
                          className="p-2 rounded-full hover:bg-gray-200 transition-colors flex items-center justify-center"
                          title="Download Declaration"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-brand-headingBlue"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                            />
                          </svg>
                        </a>
                      )}
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Declaration submitted by bidder for KYC name mismatch.
                    </p>
                    <div className="bg-white border-gray-200 rounded-sm px-3 py-1 mt-3">
                      <p className="text-xs font-medium text-gray-700 mb-1">
                        Reason for Name Mismatch:
                      </p>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        {data?.data?.name_mismatch_reason}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {data?.data?.user_type !== "individual" &&
                data?.data?.is_joint !== true && (
                  <div className="mt-1.5">
                    <div className="text-[15px] font-semibold mb-2">
                      Authorised Representative Details
                    </div>
                    <div>
                      <div className="mb-2.5 grid grid-cols-2 gap-y-1">
                        <div className="text-xs text-[#666]">
                          AR Name:{" "}
                          <span className="text-[#888]">
                            {maskName(data?.data?.ar_details?.pan_name)}
                          </span>
                        </div>
                        <div className="text-xs text-[#666]">
                          AR Email:{" "}
                          <span className="text-[#888]">
                            {maskEmail(data?.data?.ar_details?.email)}
                          </span>
                        </div>
                        <div className="text-xs text-[#666]">
                          AR PAN:{" "}
                          <span className="text-[#888]">
                            {maskPan(data?.data?.ar_details?.pan)}
                          </span>
                        </div>
                        <div className="text-xs text-[#666]">
                          AR Mobile:{" "}
                          <span className="text-[#888]">
                            {maskPhone(data?.data?.ar_details?.user_mobile)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center p-1 rounded-md bg-[#fbfbfb] mb-2">
                      <div>
                        <div className="font-semibold text-sm">
                          AR KYC Documents
                        </div>
                        <div className="text-xs text-[#777] mt-0.5">
                          Masked docs of the Authorised Representative are
                          displayed.
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={async () =>
                            await handleViewDocuments("ar_kyc_doc")
                          }
                          className="w-8 h-8 rounded-full border border-[#ddd] flex items-center justify-center bg-white hover:bg-[#fafafa] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
                          disabled={
                            !data?.data?.ar_docs ||
                            data?.data?.ar_docs.length === 0
                          }
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={async () =>
                            await handleDownloadDocuments("ar_kyc_doc")
                          }
                          className="w-8 h-8 rounded-full border border-[#ddd] flex items-center justify-center bg-white hover:bg-[#fafafa] ml-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
                          disabled={
                            !data?.data?.ar_docs ||
                            data?.data?.ar_docs.length === 0
                          }
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              {data?.data?.user_type !== "individual" &&
                data?.data?.is_joint !== true && (
                  <div className="mt-1.5">
                    <div className="flex justify-between items-center p-1 rounded-md bg-[#fbfbfb] mb-2">
                      <div>
                        <div className="font-semibold text-sm">
                          Authorization Letters
                        </div>
                        <div className="text-xs text-[#777] mt-0.5">
                          Supporting documents for KYC.
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={async () =>
                            await handleViewDocuments("authorization_letters")
                          }
                          className="w-8 h-8 rounded-full border border-[#ddd] flex items-center justify-center bg-white hover:bg-[#fafafa] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
                          disabled={
                            !data?.data?.ar_authorization_letter_docs ||
                            data?.data?.ar_authorization_letter_docs?.length ===
                              0
                          }
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={async () =>
                            await handleDownloadDocuments(
                              "authorization_letters",
                            )
                          }
                          className="w-8 h-8 rounded-full border border-[#ddd] flex items-center justify-center bg-white hover:bg-[#fafafa] ml-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
                          disabled={
                            !data?.data?.ar_authorization_letter_docs ||
                            data?.data?.ar_authorization_letter_docs?.length ===
                              0
                          }
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              {data?.data?.user_type !== "individual" &&
                data?.data?.is_joint !== true && (
                  <div className="mt-1.5">
                    <div className="flex justify-between items-center p-1 rounded-md bg-[#fbfbfb] mb-2">
                      <div>
                        <div className="font-semibold text-sm">
                          Other Supporting Documents
                        </div>
                        <div className="text-xs text-[#777] mt-0.5">
                          Supporting documents for KYC.
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={async () =>
                            await handleViewDocuments("other_docs")
                          }
                          className="w-8 h-8 rounded-full border border-[#ddd] flex items-center justify-center bg-white hover:bg-[#fafafa] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
                          disabled={
                            !data?.data?.additional_docs ||
                            data?.data?.additional_docs?.length === 0
                          }
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={async () =>
                            await handleDownloadDocuments("other_docs")
                          }
                          className="w-8 h-8 rounded-full border border-[#ddd] flex items-center justify-center bg-white hover:bg-[#fafafa] ml-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
                          disabled={
                            !data?.data?.additional_docs ||
                            data?.data?.additional_docs?.length === 0
                          }
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>

        {/* Action Buttons Section */}
        <div className="sticky bottom-0 left-0 bg-white p-2 md:p-0 border-t md:border-t-0 rounded-lg md:rounded-none w-full md:static">
          <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6 md:mt-6">
            <Button
              className="w-full"
              disabled={loading}
              onClick={async () => {
                // await handleApprove();
                setIsApproveModalOpen(true);
                setApproveRemark("");
              }}
            >
              Approve
            </Button>
            <Button
              variant="ghost"
              disabled={loading}
              className="w-full bg-brand-bgGrey"
              onClick={handleRejectClick}
            >
              Reject
            </Button>
          </div>
        </div>
      </div>

      {/* Document View Modal */}
      <DocumentViewerModal
        isOpen={isDocumentModalOpen}
        headingText={modelHeading}
        onClose={() => {
          setIsDocumentModalOpen(false);
          setDocumentUrls([]);
          setCurrentDocumentIndex(0);
        }}
        documentUrls={documentUrls}
        currentDocumentIndex={currentDocumentIndex}
        onPrevious={() => {
          setCurrentDocumentIndex((prev) =>
            prev > 0 ? prev - 1 : documentUrls.length - 1,
          );
        }}
        onNext={() => {
          setCurrentDocumentIndex((prev) =>
            prev < documentUrls.length - 1 ? prev + 1 : 0,
          );
        }}
      />

      {/* Download Modal */}
      <DocumentDownloadModalV1
        isDownloadModalOpen={isDownloadModalOpen}
        downloadDocumentType={downloadDocumentType}
        documentUrls={documentUrls}
        setDocumentUrls={setDocumentUrls}
        setIsDownloadModalOpen={setIsDownloadModalOpen}
        setDownloadDocumentType={setDownloadDocumentType}
        handleDownloadFile={handleDownloadFile}
      />

      {/* Reject Modal */}
      <RejectModalV1
        isRejectModalOpen={isRejectModalOpen}
        setIsRejectModalOpen={setIsRejectModalOpen}
        rejectRemark={rejectRemark}
        setRejectRemark={setRejectRemark}
        loading={loading}
        handleReject={handleReject}
      />
      <KYCApprovalModel
        isApproveModalOpen={isApproveModalOpen}
        setIsApproveModalOpen={setIsApproveModalOpen}
        approveRemark={approveRemark}
        setApproveRemark={setApproveRemark}
        handleApprove={handleApprove}
        loading={loading}
      />
    </div>
  );
}

interface DocumentDownloadModalV1Props {
  isDownloadModalOpen: boolean;
  downloadDocumentType: any;
  documentUrls: any;
  setDocumentUrls: any;
  setIsDownloadModalOpen: any;
  setDownloadDocumentType: any;
  handleDownloadFile: any;
}
const DocumentDownloadModalV1 = ({
  isDownloadModalOpen,
  downloadDocumentType,
  documentUrls,
  setDocumentUrls,
  setIsDownloadModalOpen,
  setDownloadDocumentType,
  handleDownloadFile,
}: DocumentDownloadModalV1Props) => {
  const handleClose = () => {
    setIsDownloadModalOpen(false);
    setDownloadDocumentType(null);
    setDocumentUrls([]);
  };
  const extractFileId = (file: string | { id?: string } | any): string => {
    if (typeof file === "string") return file;
    return file?.id || file || "";
  };
  return (
    <CustomModalLayoutV2
      open={isDownloadModalOpen}
      onClose={handleClose}
      blurBackground={true}
      className="!w-full mx-2 sm:mx-4 md:mx-6 max-w-md overflow-hidden"
      childrenClassName="p-0 overflow-hidden flex flex-col"
    >
      <div className="flex flex-col max-h-[80vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 border-b flex-shrink-0 bg-white">
          <h3 className="text-lg sm:text-xl font-semibold text-brand-textBlack">
            Download{" "}
            {downloadDocumentType === "id"
              ? "ID Proof"
              : downloadDocumentType === "address"
              ? "Address Proof"
              : downloadDocumentType === "ar_kyc_doc"
              ? "AR KYC"
              : downloadDocumentType === "authorization_letters"
              ? "Authorization Letters"
              : downloadDocumentType === "other_docs"
              ? "Other Supporting"
              : "KYC"}{" "}
            Documents
          </h3>
        </div>

        {/* Files List */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {(() => {
            let documentTypeLabel = "";

            if (downloadDocumentType === "id") {
              documentTypeLabel = "ID Proof";
            } else if (downloadDocumentType === "address") {
              documentTypeLabel = "Address Proof";
            } else if (downloadDocumentType === "ar_kyc_doc") {
              documentTypeLabel = "AR KYC Document";
            } else if (downloadDocumentType === "authorization_letters") {
              documentTypeLabel = "Authorization Letter";
            } else if (downloadDocumentType === "other_docs") {
              documentTypeLabel = "Other Supporting Document";
            }

            return documentUrls && documentUrls.length > 0 ? (
              <div className="space-y-3">
                {documentUrls.map((file: string, index: number) => {
                  const fileId = extractFileId(file);
                  return (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="flex-shrink-0">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6 text-brand-headingBlue"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-brand-textBlack truncate">
                            {documentTypeLabel} {index + 1}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {fileId}
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleDownloadFile(fileId, index)}
                        className="ml-3 flex-shrink-0"
                        size="sm"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 mr-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                          />
                        </svg>
                        Download
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-gray-500">
                <p>No documents available</p>
              </div>
            );
          })()}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-t bg-white flex-shrink-0">
          <Button onClick={() => {}} className="w-full" variant="outline">
            Close
          </Button>
        </div>
      </div>
    </CustomModalLayoutV2>
  );
};
interface RejectModalV1Props {
  isRejectModalOpen: boolean;
  setIsRejectModalOpen: any;
  rejectRemark: string;
  setRejectRemark: any;
  loading: boolean;
  handleReject: any;
}
const RejectModalV1 = (props: RejectModalV1Props) => {
  const {
    isRejectModalOpen,
    setIsRejectModalOpen,
    rejectRemark,
    setRejectRemark,
    loading,
    handleReject,
  } = props;
  return (
    <CustomModalLayoutV2
      open={isRejectModalOpen}
      onClose={() => {
        if (!loading) {
          setIsRejectModalOpen(false);
          setRejectRemark("");
        }
      }}
      blurBackground={true}
      className="!w-full mx-2 sm:mx-4 md:mx-6 max-w-md overflow-hidden"
      childrenClassName="p-0 overflow-hidden flex flex-col"
    >
      <div className="flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 border-b flex-shrink-0 bg-white">
          <h3 className="text-lg sm:text-xl font-semibold text-brand-textBlack">
            Reject KYC Request
          </h3>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Remark <span className="text-red-500">*</span>
            </label>
            <Textarea
              placeholder="Please provide a reason for rejection"
              value={rejectRemark}
              onChange={(e) => setRejectRemark(e.target.value)}
              className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded min-h-[100px]"
              disabled={loading}
            />
            {!rejectRemark.trim() && (
              <p className="text-xs text-red-500 mt-1">
                Remark is required to reject the request
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-t bg-white flex-shrink-0 flex gap-3">
          <Button
            onClick={() => {
              setIsRejectModalOpen(false);
              setRejectRemark("");
            }}
            className="flex-1"
            variant="outline"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            className="flex-1 bg-red-600 hover:bg-red-700"
            disabled={loading || !rejectRemark.trim()}
          >
            {loading ? "Rejecting..." : "Confirm Reject"}
          </Button>
        </div>
      </div>
    </CustomModalLayoutV2>
  );
};

interface KYCApprovalModelProps {
  isApproveModalOpen: boolean;
  setIsApproveModalOpen: any;
  loading: boolean;
  handleApprove: any;
  setApproveRemark: any;
  approveRemark: string;
}
const KYCApprovalModel = ({
  isApproveModalOpen,
  setIsApproveModalOpen,
  loading,
  handleApprove,
  setApproveRemark,
  approveRemark,
}: KYCApprovalModelProps) => {
  return (
    <CustomModalLayoutV2
      open={isApproveModalOpen}
      onClose={() => {
        if (!loading) {
          setIsApproveModalOpen(false);
          setApproveRemark("");
        }
      }}
      blurBackground={true}
      className="!w-full mx-2 sm:mx-4 md:mx-6 max-w-md overflow-hidden"
      childrenClassName="p-0 overflow-hidden flex flex-col"
    >
      <div className="flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 border-b flex-shrink-0 bg-white">
          <h3 className="text-lg sm:text-xl font-semibold text-brand-textBlack">
            Approve KYC Request
          </h3>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Remarks (Optional)
            </label>
            <Textarea
              placeholder="Please provide a Remark for approval"
              value={approveRemark}
              onChange={(e) => setApproveRemark(e.target.value)}
              className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded min-h-[70px]"
              disabled={loading}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-t bg-white flex-shrink-0 flex gap-3">
          <Button
            onClick={handleApprove}
            className="flex-1 bg-green-600 hover:bg-green-700"
            disabled={loading}
          >
            {loading ? "Approving..." : "Confirm Approve"}
          </Button>
          <Button
            onClick={() => {
              setIsApproveModalOpen(false);
              setApproveRemark("");
            }}
            className="flex-1"
            variant="outline"
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </div>
    </CustomModalLayoutV2>
  );
};
